/**
 * Смена автоакцепта (`/activity/accept/:userId?date=ГГГГ-ММ-ДД`, право `accept_view`): день сотрудника салона
 * по правилу его группы — шкала (статусы, акцепт CRM, автоакцепт), периоды «автоакцепт прошёл» / «требует
 * решения» с причиной, лента действий `sUser_activity` с разрывами, как `timeActivityList` Резерва. Только
 * просмотр: акцепт ставят в Резерве по ссылке. Спека `docs/specs/2026-11-05-activity-autoaccept-design.md`,
 * «Экран 2»; числа — те же, что у `prokat-runner accept-review --user ID --date Д`.
 */
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useState } from "react";
import { Link, useParams, useSearchParams } from "react-router";

import { Tile } from "@/components/activity/DaysView";
import { ShiftBar } from "@/components/activity/ShiftBar";
import {
  ACCEPTED_BY,
  type AcceptAction,
  type AcceptDayDetail,
  loadAcceptDay,
  reasonText,
  retryServerErrors,
  type Segment,
  VERDICT,
} from "@/lib/accept";
import { ACCEPT_VIEW, useCan } from "@/lib/access";
import { addDays, dm, weekday } from "@/lib/delivery";
import { workTime, yesterday } from "@/lib/leads";

const hm = (iso: string) => iso.slice(11, 16);
const hms = (iso: string) => iso.slice(11, 19);
const ПОКАЗАТЬ = 60;
const РАЗРЫВ = 20;

/** Ссылки на объекты действия: серия — карточка товара CRM, задача — Резерв, клиент — карточка CRM. */
const SERIAL_URL = "https://crm-rf.unirenter.ru/#/info?serial=";
const TASK_URL = "https://a.unirenter.ru/b24/r.php?page=moveTask&do=moveTaskList&moveTaskID=";
const CUSTOMER_URL = "https://crm.unirenter.ru/#/customers?section=customerCard&tab=customerInfo&customerID=";

/** Полоска слева у строки ленты — цветом сегмента, как на шкале. */
function stripe(s: Segment | undefined): string {
  if (!s) return "border-l-transparent";
  if (s.kind === "auto") return "border-l-ok";
  if (s.reason === "idle" || s.reason === "idle_commented") return "border-l-warn";
  if (s.reason === "piece" || s.reason === "open") return "border-l-fg-faint";
  return "border-l-err";
}

const ext = "text-accent-text hover:underline";

function About({ a }: { a: AcceptAction }) {
  const parts = [
    a.comment && <span key="c" className="whitespace-pre-wrap break-words">{a.comment}</span>,
    a.tasks && <span key="t" className="text-fg-muted">задачи: {a.tasks}</span>,
    a.serial && (
      <a key="s" href={`${SERIAL_URL}${a.serial}`} target="_blank" rel="noreferrer" className={ext} title="товар в CRM">
        серия {a.serial}
      </a>
    ),
    a.move_task_id && (
      <a key="m" href={`${TASK_URL}${a.move_task_id}`} target="_blank" rel="noreferrer" className={ext} title="задача в Резерве">
        задача {a.move_task_id}
      </a>
    ),
    a.customer_id && (
      <a key="k" href={`${CUSTOMER_URL}${a.customer_id}`} target="_blank" rel="noreferrer" className={ext} title="клиент в CRM">
        клиент {a.customer_id}
      </a>
    ),
  ].filter(Boolean);
  if (!parts.length) return <span className="text-fg-faint">—</span>;
  return <span className="flex flex-wrap gap-x-2 gap-y-0.5">{parts}</span>;
}

function Actions({ d }: { d: AcceptDayDetail }) {
  const [all, setAll] = useState(false);
  const rows = all ? d.actions : d.actions.slice(0, ПОКАЗАТЬ);
  return (
    <section className="rounded-[var(--radius-card)] border border-border bg-bg-subtle" data-annot="действия за день">
      <div className="flex items-baseline gap-2 px-4 pt-3">
        <span className="text-[13px] font-medium">Действия за день</span>
        <span className="text-[12px] text-fg-faint">
          {d.actions.length} строк CRM · полоска слева — сегмент шкалы, разрыв — от прошлого действия (начало смены,
          перерыв и «Ушел» — тоже границы), красный — от {РАЗРЫВ} минут; смены статуса серым
        </span>
      </div>
      {d.actions.length === 0 ? (
        <div className="p-4 text-[12.5px] text-fg-muted">Действий в CRM за день нет.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="mt-2 w-full text-[12.5px]">
            <thead className="text-left text-[11.5px] text-fg-faint">
              <tr>
                <th className="px-4 py-1.5 font-medium">Время</th>
                <th className="px-2.5 py-1.5 text-right font-medium">Разрыв</th>
                <th className="px-2.5 py-1.5 font-medium">Действие</th>
                <th className="px-2.5 py-1.5 font-medium">О действии</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((a) => {
                const seg = a.segment === null ? undefined : d.day.segments[a.segment];
                const gap = a.gap_min ?? 0;
                return (
                  <tr
                    key={a.id}
                    className={`border-t border-border align-top ${a.is_status ? "text-fg-faint" : ""}`}
                    title={seg ? `${seg.kind === "auto" ? "автоакцепт" : `требует решения: ${reasonText(seg)}`} ${hm(seg.from)}–${hm(seg.to)}` : undefined}
                  >
                    <td className={`whitespace-nowrap border-l-[3px] px-4 py-1.5 tabular-nums ${stripe(seg)}`}>{hms(a.at)}</td>
                    <td className="whitespace-nowrap px-2.5 py-1.5 text-right tabular-nums">
                      {a.gap_min === null || gap < 1 ? (
                        ""
                      ) : gap >= РАЗРЫВ ? (
                        <span className="rounded-[var(--radius-ui)] bg-err-bg px-1.5 py-0.5 text-err">{workTime(gap)}</span>
                      ) : (
                        <span className="text-fg-faint">{workTime(gap)}</span>
                      )}
                    </td>
                    <td className="px-2.5 py-1.5">
                      {a.name}
                      {!a.is_status && !a.is_trace && (
                        <span className="ml-1 text-[11.5px] text-fg-faint" title="ставит выгрузка раз в час — следом работы не считается">
                          · не след
                        </span>
                      )}
                    </td>
                    <td className="max-w-[640px] px-2.5 py-1.5">
                      <About a={a} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {!all && d.actions.length > ПОКАЗАТЬ && (
        <div className="px-4 py-2">
          <button
            type="button"
            onClick={() => setAll(true)}
            className="h-7 rounded-[var(--radius-ui)] border border-border px-2.5 text-[12px] text-fg hover:bg-bg-hover"
          >
            Показать все {d.actions.length}
          </button>
        </div>
      )}
    </section>
  );
}

function Periods({ d }: { d: AcceptDayDetail }) {
  const day = d.day;
  const auto = day.segments.filter((s) => s.kind === "auto");
  const manual = day.segments.filter((s) => s.kind === "manual");
  const robot = day.verdict === "auto" || day.verdict === "partial";
  const row = "flex flex-wrap items-baseline gap-x-3 border-t border-border px-4 py-1.5 first:border-t-0";
  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <section className="rounded-[var(--radius-card)] border border-border bg-bg-subtle" data-annot="автоакцепт прошёл">
        <div className="px-4 pb-1 pt-3 text-[13px] font-medium">
          Автоакцепт прошёл <span className="font-normal text-fg-faint">{workTime(day.auto_min)}</span>
        </div>
        {auto.length === 0 ? (
          <div className="px-4 pb-3 text-[12.5px] text-fg-muted">Ничего: день целиком решает HR.</div>
        ) : (
          <div className="pb-2 text-[12.5px]">
            {auto.map((s) => (
              <div key={s.from} className={row}>
                <span className="tabular-nums">
                  {hm(s.from)}–{hm(s.to)}
                </span>
                <span className="tabular-nums text-fg-muted">{workTime(s.minutes)}</span>
                <span className={s.in_crm ? "text-ok" : "text-fg-faint"}>
                  {s.in_crm ? "в CRM уже стоит" : robot ? "поставил бы Робот" : "ещё не в CRM"}
                </span>
              </div>
            ))}
          </div>
        )}
      </section>
      <section className="rounded-[var(--radius-card)] border border-border bg-bg-subtle" data-annot="требует решения">
        <div className="flex items-baseline gap-2 px-4 pb-1 pt-3 text-[13px] font-medium">
          Требует решения <span className="font-normal text-fg-faint">{workTime(day.manual_min)} · {manual.length} окон</span>
          <a href={d.links.reserve} target="_blank" rel="noreferrer" className="ml-auto text-[12px] font-normal text-accent-text hover:underline">
            в Резерве ↗
          </a>
        </div>
        {manual.length === 0 ? (
          <div className="px-4 pb-3 text-[12.5px] text-fg-muted">Окон нет.</div>
        ) : (
          <div className="pb-2 text-[12.5px]">
            {manual.map((s) => (
              <div key={s.from} className={row}>
                <span className="tabular-nums">
                  {hm(s.from)}–{hm(s.to)}
                </span>
                <span className="tabular-nums text-fg-muted">{workTime(s.minutes)}</span>
                <span className={s.reason === "idle" || s.reason === "idle_commented" ? "text-warn" : "text-err"}>{reasonText(s)}</span>
                {s.in_crm && <span className="text-fg-faint">· в CRM принято</span>}
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function Body({ d }: { d: AcceptDayDetail }) {
  const day = d.day;
  const by = day.accepted_by ? ACCEPTED_BY[day.accepted_by] : null;
  const billed = d.billed === null ? "день не начислен" : `начислено ${d.billed.toLocaleString("ru-RU")} ₽`;
  return (
    <div className="flex flex-col gap-4">
      <div className="text-[12.5px] text-fg-muted" data-annot="правило">
        <span className={`mr-2 inline-block rounded-[var(--radius-ui)] px-1.5 py-0.5 text-[11.5px] ${VERDICT[day.verdict].tone}`} title={VERDICT[day.verdict].hint}>
          {VERDICT[day.verdict].label}
        </span>
        {d.rule_text}
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Tile label="По статусам" value={workTime(day.paid_min)} hint={day.leave ? `«Ушел» в ${hm(day.leave)}` : "«Ушел» за день нет"} />
        <Tile label="Автоакцепт" value={workTime(day.auto_min)} hint={`не в CRM ${workTime(day.auto_pending_min)}`} />
        <Tile label="Требует решения" value={workTime(day.manual_min)} hint={`${day.manual_count} окон`} />
        <Tile
          label="Принято в CRM"
          value={day.accepted_min ? workTime(day.accepted_min) : "нет"}
          hint={`${by ? `${by}` : "акцепта нет"} · ${billed}`}
          extra={
            day.accepted.length > 0
              ? day.accepted.map((r) => `${hm(r.from)}–${hm(r.to)} ${r.by_name ?? `#${r.by_id ?? "?"}`}`).join(", ")
              : undefined
          }
        />
      </div>
      <ShiftBar
        size="lg"
        segments={day.segments}
        statuses={d.statuses}
        accepted={day.accepted}
        totals={{
          statuses: workTime(day.paid_min),
          accepted: day.accepted_min ? `${workTime(day.accepted_min)}${by ? ` · ${by}` : ""}` : "нет",
          auto: workTime(day.auto_min),
        }}
      />
      <Periods d={d} />
      <Actions d={d} />
    </div>
  );
}

export function AcceptDayPage() {
  const user = Number(useParams().userId);
  const [params, setParams] = useSearchParams();
  const day = params.get("date") || yesterday();
  const can = useCan(ACCEPT_VIEW);
  const q = useQuery({
    queryKey: ["activity-accept-day", user, day],
    queryFn: () => loadAcceptDay(user, day),
    enabled: can && Number.isInteger(user),
    retry: retryServerErrors,
  });
  const go = (n: number) => setParams({ date: addDays(day, n) });
  const nav = "flex size-8 items-center justify-center rounded-[var(--radius-ui)] border border-border hover:bg-bg-hover disabled:opacity-40";
  const link = "text-[12.5px] text-accent-text hover:underline";

  return (
    <main className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-5">
      <div className="flex flex-wrap items-center gap-3" data-annot="шапка смены автоакцепта">
        <Link to="/activity?view=accept" className="text-[12.5px] text-fg-muted hover:text-fg">
          ← Автоакцепт
        </Link>
        <h1 className="text-[16px] font-semibold tracking-[-0.015em]">{q.data ? q.data.day.user.name : `Сотрудник ${user}`}</h1>
        {q.data && (
          <span className="text-[12.5px] text-fg-faint">
            {q.data.day.location ?? "—"} · {q.data.rule_label}
          </span>
        )}
        <div className="ml-auto flex items-center gap-2">
          <button type="button" aria-label="Предыдущий день" onClick={() => go(-1)} className={nav}>
            <ChevronLeft size={15} />
          </button>
          <span className="min-w-[88px] text-center text-[12.5px] tabular-nums">
            {dm(day)} <span className="text-fg-faint">{weekday(day)}</span>
          </span>
          <button type="button" aria-label="Следующий день" disabled={day >= addDays(yesterday(), 1)} onClick={() => go(1)} className={nav}>
            <ChevronRight size={15} />
          </button>
        </div>
        {q.data && (
          <div className="flex w-full flex-wrap gap-x-4 gap-y-1" data-annot="ссылки смены">
            <Link to={q.data.links.shift} className={link} title="смена в «Активности»: статусы, звонки, сообщения, действия «Чатов»">
              Смена КЦ
            </Link>
            <a href={q.data.links.reserve} target="_blank" rel="noreferrer" className={link} title="лента действий дня в Резерве — там HR ставит и снимает акцепт">
              Открыть в Резерве ↗
            </a>
            <a href={q.data.links.reserve_time} target="_blank" rel="noreferrer" className={link} title="время по статусам и акцепт в Резерве">
              Время в Резерве ↗
            </a>
          </div>
        )}
      </div>
      {!can ? (
        <div className="text-[12.5px] text-fg-muted">Нет права «Автоакцепт времени».</div>
      ) : q.isPending ? (
        <div className="text-[12.5px] text-fg-muted">Читаю статусы, акцепт и действия из CRM…</div>
      ) : q.isError ? (
        <div className="text-[12.5px] text-err">Не удалось получить смену: {String(q.error.message)}</div>
      ) : (
        <Body d={q.data} />
      )}
    </main>
  );
}
